import Party from './Party'
import PartyResult from './PartyResult'
import { ElectionOutcome, IPartyNumberMap, IPartyStringMap } from './TypeHelper'

export interface IPartyResultDict {
  [partyKey: string]: PartyResult
}

export default class PartyResultMap {
  protected _partyResultMap: IPartyResultDict = {}

  public clone(): PartyResultMap {
    const clone = new PartyResultMap()
    this.results().forEach((partyResult): void => {
      clone.setResult(partyResult.clone())
    })
    return clone
  }

  public setResult(partyResult: PartyResult): void {
    this._partyResultMap[partyResult.party.key] = partyResult
  }

  public results(): PartyResult[] {
    return Object.values(this._partyResultMap)
  }

  public resultForParty(party: Party): PartyResult | undefined {
    return this.resultForPartyKey(party.key)
  }

  public resultForPartyKey(partyKey: string): PartyResult | undefined {
    const partyResult = this._partyResultMap[partyKey]
    return partyResult
  }

  public partyKeys(): string[] {
    return Object.keys(this._partyResultMap)
  }

  protected _resultsBy(
    sortMethod: (a: PartyResult, b: PartyResult) => number
  ): PartyResult[] {
    return this.results().sort(sortMethod)
  }

  public resultsBySeatCount(): PartyResult[] {
    return this._resultsBy((a, b): number => b.seatCount - a.seatCount)
  }

  public resultsByVotePercent(): PartyResult[] {
    return this._resultsBy((a, b): number => b.votePercent - a.votePercent)
  }

  // TODO: Make IPartyNumberMap a class
  protected partyMap<T extends string | number>(
    accessor: (r: PartyResult) => T
  ): { [key: string]: T } {
    const partyObject: { [key: string]: T } = {}
    this.partyKeys().forEach((partyKey): void => {
      const result = this.resultForPartyKey(partyKey)
      if (result) partyObject[partyKey] = accessor(result)
    })
    return partyObject
  }

  // Kinda hacky code from https://jsbin.com/lecokaw/edit?js,console via
  // https://stackoverflow.com/a/30870577
  protected subsetSum(partyResults: PartyResult[]): PartyResult[][] {
    const target = 170
    const combos: PartyResult[][] = []

    function recurse(
      partyResults: PartyResult[],
      partial: PartyResult[]
    ): void {
      // sum partial
      const s = partial.reduce((a, b): number => {
        return a + b.seatCount
      }, 0)

      // check if the partial sum is equals to target
      // and push to possible combinations array
      if (s >= target) {
        combos.push(partial)
        return
      }

      partyResults.forEach((n, i): void => {
        recurse(partyResults.slice(i + 1), partial.concat(partyResults[i]))
      })
    }

    recurse(partyResults, [])

    return combos
  }

  public findMajorities(): PartyResult[][] {
    // Results must be sorted by seat count!
    return this.subsetSum(this.resultsBySeatCount())
  }

  public voteCounts(): IPartyNumberMap {
    return this.partyMap((r): number => r.voteCount)
  }

  public seatCounts(): IPartyNumberMap {
    return this.partyMap((r): number => r.seatCount)
  }

  public votePercents(): IPartyNumberMap {
    return this.partyMap((r): number => r.votePercent)
  }

  public seatPercents(): IPartyNumberMap {
    return this.partyMap((r): number => r.seatPercent)
  }

  public outcomes(): IPartyStringMap {
    return this.partyMap((r): ElectionOutcome => r.outcome)
  }

  public map(): IPartyResultDict {
    return this._partyResultMap
  }
}
